import { useState } from "react"
import toast from "react-hot-toast"
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Circle,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Clock,
  Bell,
} from "lucide-react"

import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"

function getStatusConfig(status: string) {
  switch (status) {
    case "operational":
      return { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", label: "Operational" }
    case "degraded_performance":
      return { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Degraded" }
    case "partial_outage":
      return { icon: AlertTriangle, color: "text-orange-500", bg: "bg-orange-500/10", label: "Partial Outage" }
    case "major_outage":
      return { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Major Outage" }
    default:
      return { icon: Circle, color: "text-muted-foreground", bg: "bg-secondary", label: "Unknown" }
  }
}

const CATEGORY_LABELS: Record<string, string> = {
  ai: "AI Providers",
  infra: "Infrastructure",
  data: "Data & Storage",
  services: "Services",
  other: "Other",
}

function VendorStatusScreen() {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["ai", "infra", "data", "services", "other"]),
  )
  const [showAddForm, setShowAddForm] = useState(false)
  const [pollingVendors, setPollingVendors] = useState<Record<string, boolean>>({})

  const { data, isLoading, refetch } = trpc.vendorStatus.list.useQuery()
  const pollAllMutation = trpc.vendorStatus.pollAll.useMutation({
    onSuccess: (result) => {
      toast.success(`Polled ${result.checked} vendors (${result.changed} changed)`)
      void refetch()
    },
    onError: (err) => toast.error(err.message || "Failed to poll vendors"),
  })
  const seedMutation = trpc.vendorStatus.seed.useMutation({
    onSuccess: (result) => {
      if (result.seeded > 0) {
        toast.success(`Seeded ${result.seeded} default vendors`)
      } else {
        toast.success("Vendors already exist")
      }
      void refetch()
    },
    onError: (err) => toast.error(err.message || "Failed to seed vendors"),
  })
  const pollOneMutation = trpc.vendorStatus.pollOne.useMutation({
    onSuccess: (result, variables: any) => {
      setPollingVendors((prev) => ({ ...prev, [variables.id]: false }))
      toast.success(`${result.vendor}: ${result.status}`)
      void refetch()
    },
    onError: (err, variables: any) => {
      setPollingVendors((prev) => ({ ...prev, [variables.id]: false }))
      toast.error(err.message || "Failed to poll vendor")
    },
  })
  const removeMutation = trpc.vendorStatus.remove.useMutation({
    onSuccess: (result) => {
      toast.success(`Removed ${result.name}`)
      void refetch()
    },
    onError: (err) => toast.error(err.message || "Failed to remove vendor"),
  })
  const testAlertMutation = trpc.vendorStatus.testAlert.useMutation({
    onSuccess: () => toast.success("Test alert sent to #alert-vendor"),
    onError: (err) => toast.error(err.message || "Failed to send test alert"),
  })

  const toggleCategory = (cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const isEmpty = data && data.vendors.length === 0

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">Vendor Status</h1>
            <p className="text-sm text-muted-foreground">External service health monitoring with Slack alerts</p>
          </div>
          <div className="flex items-center gap-2">
            {isEmpty && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => seedMutation.mutate()}
                loading={seedMutation.isLoading}
              >
                <Plus className="h-4 w-4 mr-1" />
                Seed Defaults
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(!showAddForm)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Vendor
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => testAlertMutation.mutate()}
              loading={testAlertMutation.isLoading}
            >
              <Bell className="h-4 w-4 mr-1" />
              Test Alert
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => pollAllMutation.mutate()}
              loading={pollAllMutation.isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Poll All
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-3 gap-4">
            <SummaryCard label="TOTAL" count={data.summary.total} color="blue" />
            <SummaryCard label="OPERATIONAL" count={data.summary.operational} color="green" />
            <SummaryCard label="ISSUES" count={data.summary.issues} color="red" />
          </div>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <AddVendorForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false)
            void refetch()
          }}
        />
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3" />
            Loading vendor statuses...
          </div>
        ) : isEmpty ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No vendors configured yet.</p>
            <Button onClick={() => seedMutation.mutate()} loading={seedMutation.isLoading}>
              <Plus className="h-4 w-4 mr-2" />
              Seed Default Vendors
            </Button>
          </div>
        ) : (
          Object.entries(data.byCategory).map(([category, vendors]) => (
            <div key={category} className="mb-4">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-2 w-full text-left py-2 hover:bg-secondary rounded px-2 transition-colors"
              >
                {expandedCategories.has(category) ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="font-semibold text-foreground">{CATEGORY_LABELS[category] || category}</span>
                <span className="text-xs text-muted-foreground">({vendors.length})</span>
                <CategoryStatusDots vendors={vendors} />
              </button>

              {/* Vendor Rows */}
              {expandedCategories.has(category) && (
                <div className="ml-6 space-y-1 mt-1">
                  {vendors.map((vendor) => (
                    <VendorRow
                      key={vendor.id}
                      vendor={vendor}
                      onPoll={() => {
                        setPollingVendors((prev) => ({ ...prev, [vendor.id]: true }))
                        pollOneMutation.mutate({ id: vendor.id })
                      }}
                      onRemove={() => {
                        if (window.confirm(`Are you sure you want to remove ${vendor.name}?`)) {
                          removeMutation.mutate({ id: vendor.id })
                        }
                      }}
                      isPolling={pollingVendors[vendor.id] ?? false}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SummaryCard({ label, count, color }: { label: string; count: number; color: "blue" | "green" | "red" }) {
  const colorClasses = {
    blue: "border-l-blue-500 bg-blue-500/10 text-blue-500",
    green: "border-l-green-500 bg-green-500/10 text-green-500",
    red: "border-l-red-500 bg-red-500/10 text-red-500",
  }

  return (
    <div className={`rounded-lg p-3 border-l-4 ${colorClasses[color]}`}>
      <div className="text-xs font-medium">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{count}</div>
    </div>
  )
}

function CategoryStatusDots({ vendors }: { vendors: { id: string; name: string; currentStatus: string }[] }) {
  return (
    <div className="flex items-center gap-1 ml-2">
      {vendors.map((v) => {
        const config = getStatusConfig(v.currentStatus)
        return <div key={v.id} className={`w-2 h-2 rounded-full ${config.color.replace("text-", "bg-")}`} />
      })}
    </div>
  )
}

function VendorRow({
  vendor,
  onPoll,
  onRemove,
  isPolling,
}: {
  vendor: {
    id: string
    name: string
    currentStatus: string
    currentDescription: string | null
    statusPageUrl: string
    lastCheckedAt: Date | string | null
  }
  onPoll: () => void
  onRemove: () => void
  isPolling: boolean
}) {
  const config = getStatusConfig(vendor.currentStatus)
  const StatusIcon = config.icon
  const lastChecked = vendor.lastCheckedAt
    ? new Date(vendor.lastCheckedAt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Never"

  return (
    <div className="flex items-center py-2 px-3 rounded hover:bg-secondary transition-colors group">
      {/* Status Icon + Name */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <StatusIcon className={`h-5 w-5 ${config.color} flex-shrink-0`} />
        <div className="min-w-0">
          <div className="font-medium text-foreground">{vendor.name}</div>
          <div className="text-xs text-muted-foreground truncate">{vendor.currentDescription || config.label}</div>
        </div>
      </div>

      {/* Status Badge */}
      <div className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.color} mr-4`}>{config.label}</div>

      {/* Last Checked */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground mr-4 w-32">
        <Clock className="h-3 w-3" />
        {lastChecked}
      </div>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPoll()
          }}
          disabled={isPolling}
          className="p-1.5 hover:bg-border rounded text-muted-foreground hover:text-foreground disabled:opacity-50"
          title="Poll now"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        <a
          href={vendor.statusPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 hover:bg-border rounded text-muted-foreground hover:text-foreground"
          title="Open status page"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="p-1.5 hover:bg-border rounded text-muted-foreground hover:text-red-500"
          title="Remove vendor"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

function AddVendorForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState("")
  const [category, setCategory] = useState("other")
  const [statusApiUrl, setStatusApiUrl] = useState("")
  const [statusPageUrl, setStatusPageUrl] = useState("")

  const addMutation = trpc.vendorStatus.add.useMutation({
    onSuccess: () => {
      toast.success(`Added ${name}`)
      onSuccess()
    },
    onError: (err) => toast.error(err.message || "Failed to add vendor"),
  })

  return (
    <div className="p-4 border-b border-border bg-secondary">
      <div className="grid grid-cols-2 gap-3 max-w-2xl">
        <Input placeholder="Vendor name" value={name} onChange={(e) => setName(e.target.value)} />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground"
        >
          <option value="ai">AI Providers</option>
          <option value="infra">Infrastructure</option>
          <option value="data">Data & Storage</option>
          <option value="services">Services</option>
          <option value="other">Other</option>
        </select>
        <Input
          placeholder="Status API URL (e.g., https://status.example.com/api/v2/status.json)"
          value={statusApiUrl}
          onChange={(e) => setStatusApiUrl(e.target.value)}
        />
        <Input
          placeholder="Status Page URL (e.g., https://status.example.com)"
          value={statusPageUrl}
          onChange={(e) => setStatusPageUrl(e.target.value)}
        />
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          size="sm"
          onClick={() => addMutation.mutate({ name, category, statusApiUrl, statusPageUrl })}
          loading={addMutation.isLoading}
          disabled={!name || !statusApiUrl || !statusPageUrl}
        >
          Add Vendor
        </Button>
        <Button size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default VendorStatusScreen
